import React from 'react';
import { Grid, Typography } from '@mui/material';
import SectionHeader from 'components/SectionHeader/SectionHeader';
import { OverviewDetailInterface, OverviewProps } from 'components/Overview/overview.types';
import { Tag } from 'components/Tag/Tag';
import { useDashboardContext } from 'context/dashboard.context';
import { formatDatePickerDate, formatMinutes } from 'utils/formatting';
import StepsOverview from 'components/Overview/StepsOverview';
import { ActivityInterface, StepInterface } from 'pages/Activities/activities.context';
import { themeOptions } from 'theme';
import CompleteActivity from 'pages/Activities/CompleteActivity';
import CustomIcon from 'components/Icon/CustomIcon';
import { Div } from 'components/Elements/Div/Div';
import CheckIcon from '@mui/icons-material/Check';
import styled from 'styled-components';
import { hasPermissions } from 'config/ability';
import { useAuthContext } from 'context/auth.context';
import ContentWrapper from 'components/StyledComponents/ContentWrapper';
import RichText from 'components/RichText/RichText';
import OverviewDetail from 'components/Overview/OverviewDetail/OverviewDetail';
import RowDetails from 'components/Overview/RowDetails/RowDetails';

const CompleteActivityWrapper = styled.div`
  margin-top: 1rem;
  width: 100%;
`;

const Overview = ({
  title,
  entityType,
  customDetails,
  description,
  tags,
  startDate,
  dueDate,
  estTimeComp,
  actTimeComp,
  assignedTo,
  ccRecipients,
  actions,
  steps,
  campaign,
  stores,
  activity,
  hideHeader = false,
  hideMediaComments = false,
}: OverviewProps) => {
  const { md } = useDashboardContext();
  const { ability, userTenantId } = useAuthContext();

  const details: OverviewDetailInterface[] = [
    { title: 'Description', value: description, richText: true },
    {
      title: 'Tags',
      value: tags?.map((tag) => (
        <Tag key={tag.id} color={tag.displayColour} text={tag.name} margin='0 0.75rem 0 0' />
      )),
      hide: !tags?.length,
    },
  ];

  const rowDetails: OverviewDetailInterface[] = [
    {
      title: 'Start Date',
      value: formatDatePickerDate(startDate),
      width: '6rem',
      marginRight: '3.6rem',
    },
    {
      title: 'Estimated Time to Complete',
      value: formatMinutes(estTimeComp),
      minWidth: '17rem',
    },
    {
      title: 'Due Date',
      value: formatDatePickerDate(dueDate),
      width: '6rem',
      marginRight: '3.6rem',
    },
    {
      title: 'Actual Time to Complete',
      value: formatMinutes(actTimeComp),
      minWidth: '17rem',
    },
    { title: 'Campaign', value: campaign, width: '6rem', marginRight: '3.6rem' },
    { title: 'Stores', value: stores?.join(', '), minWidth: '17rem' },
  ];

  const detailsPart2: OverviewDetailInterface[] = [
    { title: 'Assigned to', value: assignedTo?.join(', ') },
    { title: 'Cc Recipients', value: ccRecipients?.join(', ') },
  ];

  const completeActivity = (
    <CompleteActivityWrapper>
      {activity?.completedDate ? (
        <Grid item pt={5}>
          <CustomIcon
            label={`Completed on ${formatDatePickerDate(activity.completedDate)}`}
            icon={<CheckIcon />}
            color={themeOptions.colors.success900}
          />
        </Grid>
      ) : (
        <CompleteActivity activity={activity as ActivityInterface} />
      )}
    </CompleteActivityWrapper>
  );

  const detailsSection = (
    <Grid item xs={12} md={entityType === 'Activity' ? 5 : 12} padding='1rem'>
      <Grid container>
        <Grid item xs={12}>
          <Typography
            variant='h3'
            fontWeight={400}
            component='span'
            mb={4}
          >{`${entityType} Details`}</Typography>
        </Grid>
        {customDetails?.map((detail: OverviewDetailInterface) => (
          <OverviewDetail key={`${(typeof detail.value).toString()}-${detail.title}`} {...detail} />
        ))}
        {details.map((detail) => (
          <OverviewDetail key={`${(typeof detail.value).toString()}-${detail.title}`} {...detail} />
        ))}
        {entityType === 'Activity' && (
          <Div display='flex' width='100%' flexWrap='wrap'>
            <RowDetails rowDetails={rowDetails} />
          </Div>
        )}
        {detailsPart2.map((detail) => (
          <OverviewDetail key={`${(typeof detail.value).toString()}-${detail.title}`} {...detail} />
        ))}
        <Div width='100%'>{!!activity?.id && completeActivity}</Div>
      </Grid>
    </Grid>
  );

  const filteredSteps: StepInterface[] =
    steps && steps.length > 0
      ? (steps as StepInterface[]).filter((curStep) => {
          return (
            hasPermissions(ability, 'read', 'Step', {
              tenantId: userTenantId,
            }) ||
            hasPermissions(ability, 'read', 'Step', {
              stepId: curStep.id,
            })
          );
        })
      : [];

  const StepOverviewSection = !!filteredSteps?.length && (
    <Grid item xs={12} md={7}>
      <StepsOverview
        steps={filteredSteps as StepInterface[]}
        hideMediaComments={hideMediaComments}
      />
    </Grid>
  );

  return (
    <>
      {!hideHeader && (
        <SectionHeader title={title} actions={actions} margin='0 0 2.6875rem' pageHeader />
      )}
      <ContentWrapper>
        <Grid container mt={hideHeader ? 5 : undefined}>
          {md && StepOverviewSection}
          {detailsSection}
          {!md && StepOverviewSection}
        </Grid>
      </ContentWrapper>
    </>
  );
};

export default Overview;
